import { useAuth0, User } from "@auth0/auth0-react";
import { ErrorAlert } from "@components";
import { Role } from "@enums";
import { useAppDispatch, useAppSelector } from "@hooks";
import { Stack } from "@mui/material";
import {
  CareplanPage,
  CommandCenter,
  DosimetristPlanningAutomation,
  ErrorPage,
  Metrics,
  SentryErrorPage,
  DeploymentDashboardPage,
} from "@pages";
import Layout from "@pages/Layout/layout";
import LogoutOnInactivity from "@pages/LogoutOnInactivity/component/LogoutOnInactivity";
import PhysicianReviewPage from "@pages/PhysicianReview";
import { useWebApiV1GetUserPreferencesQuery } from "@providers/hop-ord-server/api";
import * as Sentry from "@sentry/react";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { api as orderApi } from "../providers/hop-ord-server/baseapi";
import { sec } from "./app/security";
import { setCredentials, setPreferredRole } from "./features/auth/auth0Slice";
import ConsumerDetailPage from "@pages/DeploymentDashboard/ConsumerDetailPage";

const INACTIVITY_TIMEOUT = parseInt(
  process.env.VITE_INACTIVITY_TIMEOUT_MINUTES || "60",
);

const userHomeRedirect = (roles: string[], accessToken: boolean): string => {
  // Return home page url for user based on role (to be redirected post login)
  // Multi roles will redirect to /order for now

  if (!accessToken) {
    return "";
  }

  const redirectTo = () => {
    if (roles?.includes(Role.DOSIMETRIST) || roles?.includes(Role.RO)) {
      return "/commandcenter";
    }
    return "*";
  };

  return redirectTo();
};

const App = () => {
  const [appUser, setAppUser] = useState<User | null>(null);
  const {
    user,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
    getIdTokenClaims,
  } = useAuth0();
  const dispatch = useAppDispatch();

  type CustomJwtPayload = JwtPayload & {
    user?: User;
    permissions?: string[];
  };

  useEffect(() => {
    if (process.env.VITE_ENVIRONMENT_TYPE !== "demo") {
      return;
    }
    // Handle tokens from URL parameters when coming from LAI
    // This is only for the demo enviroment.
    // TODO: Remove this completely.
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get("access_token");
    const idToken = urlParams.get("id_token");

    if (accessToken || idToken) {
      try {
        const tokenUser = accessToken
          ? jwtDecode<CustomJwtPayload>(accessToken)?.user || null
          : null;
        const username = accessToken
          ? jwtDecode<CustomJwtPayload>(accessToken)?.user?.name
          : "";
        const permissions = accessToken
          ? jwtDecode<CustomJwtPayload>(accessToken)?.permissions
          : [];

        const credentials = {
          user: username || "",
          token: accessToken || "",
          idToken: idToken || "",
          roles:
            permissions
              ?.filter(
                (role) => role.startsWith("role:") || role.startsWith("admin:"),
              )
              .map((role) => role as Role) || [],
          tenants:
            permissions?.filter((tenant) => tenant.startsWith("tenant:")) || [],
        };
        dispatch(setCredentials(credentials));

        // eslint-disable-next-line react-hooks/set-state-in-effect
        setAppUser(tokenUser);
        // Invalidate RTK Query cache for feature switches when user context changes
        // @ts-expect-error Base API instance doesn't carry tag types; runtime is correct
        dispatch(orderApi.util.invalidateTags(["FeatureSwitches"]));
      } catch (error) {
        console.error("Error processing tokens from URL:", error);
      }
    }
  }, []);

  useEffect(() => {
    const updateAccessToken = async () => {
      const token = await getAccessTokenSilently();
      const idToken = await getIdTokenClaims();
      sec.setAccessTokenSilently(getAccessTokenSilently);
      // Send only user sub which is unique to user in Auth0 and not PII to Sentry
      const sentry_name = `${user?.sub} | ${user?.name}`;
      Sentry.setUser({ username: sentry_name });
      const permissions = token
        ? jwtDecode<CustomJwtPayload>(token)?.permissions
        : [];
      const credentials = {
        user: user?.name || "",
        token: token || "",
        idToken: idToken?.__raw || "",
        roles:
          permissions
            ?.filter(
              (role) => role.startsWith("role:") || role.startsWith("admin:"),
            )
            .map((r) => r as Role) || [],
        tenants:
          permissions?.filter((tenant) => tenant.startsWith("tenant:")) || [],
      };
      dispatch(setCredentials(credentials));
      setAppUser(user || null);
      // Invalidate RTK Query cache for feature switches when user context changes
      // @ts-expect-error Base API instance doesn't carry tag types; runtime is correct
      dispatch(orderApi.util.invalidateTags(["FeatureSwitches"]));
    };
    if (user) {
      updateAccessToken();
    }
  }, [user, dispatch, getAccessTokenSilently, getIdTokenClaims]);

  useEffect(() => {
    if (!appUser && !isLoading && !user) {
      loginWithRedirect({
        appState: {
          returnTo: window.location.pathname,
        },
      });
    }
  }, [appUser, user, isLoading, loginWithRedirect]);

  const { roles, isAuthenticated: accessToken } = useAppSelector(
    (state) => state.auth,
  );

  // Fetch user preferences when authenticated to set preferred role
  const { data: userPreferences } = useWebApiV1GetUserPreferencesQuery(
    undefined,
    {
      skip: !accessToken,
    },
  );

  useEffect(() => {
    const preferredRole = userPreferences?.preferred_role as Role | undefined;
    if (preferredRole && [Role.RO, Role.DOSIMETRIST].includes(preferredRole)) {
      dispatch(setPreferredRole(preferredRole));
    }
  }, [userPreferences?.preferred_role, dispatch]);

  const showSkeleton = !appUser || !accessToken;

  const redirectTo = userHomeRedirect(roles, accessToken);

  const alertOpen = useAppSelector((state) => state.settings.alertOpen);

  return (
    <>
      {showSkeleton ? (
        <Stack data-testid="main-app-loading"></Stack>
      ) : (
        <Layout user={appUser} accessToken={accessToken}>
          <ErrorAlert open={alertOpen} />
          {/* Logout and redirect to inactivity page after 60 minutes of inactivity */}
          <LogoutOnInactivity idleMinutes={INACTIVITY_TIMEOUT} />
          <Routes>
            <Route path="/" element={<Navigate to={redirectTo} />} />
            <Route path="/commandcenter" element={<CommandCenter />} />
            <Route path="/deployments" element={<DeploymentDashboardPage />} />
            <Route
              path="/deployments/:tenantId"
              element={<ConsumerDetailPage />}
            />
            <Route path="/review/:orderId" element={<PhysicianReviewPage />} />
            <Route
              path="/commandcenter/:orderId/*"
              element={<DosimetristPlanningAutomation />}
            />
            <Route path="/order/:orderId" element={<CareplanPage />} />
            <Route path="/sentry/error" element={<SentryErrorPage />} />
            <Route path="/metrics" element={<Metrics />} />
            <Route path="*" element={<ErrorPage />} />
          </Routes>
        </Layout>
      )}
    </>
  );
};

export default App;
